import { Box, Typography } from '@mui/material';
import {
  Control,
  Controller,
  FieldValues,
  RegisterOptions,
} from 'react-hook-form';

import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { Block } from '@/components/TextEditor/plugins/ToolbarPlugin/ToolbarPlugin.utils';
import { TextEditor } from '@/components/TextEditor/TextEditor';
import { CONTENT_EDITABLE_ID } from '@/components/TextEditor/TextEditor.utils';
import { getFieldErrorValue } from '@/components/utils/inputUtils';
import { COLORS } from '@/styles/tokens/colors';
import { FieldNameFromFormShape } from '@/types/react-hook-form';

/** Height of one line of editor text: 15px font at 1.5 line height, rounded up. */
const ROW_HEIGHT_PX = 24;

export interface FormAwareTextEditorProps<FormShape extends FieldValues> {
  fieldName: FieldNameFromFormShape<FormShape>;
  label: string;
  required?: boolean;
  control: Control<FormShape>;
  /**
   * Minimum height of the typing area, in lines of text. When omitted, the
   * editor uses its default minimum height.
   */
  rows?: number;
  maxLength?: number;
  validation?: RegisterOptions<FormShape>['validate'];
  disabled?: boolean;
  omitBlockTypes?: Block[];
  allowTableInsert?: boolean;
  preserveNewLines?: boolean;
  resetKey?: number;
  autoFocus?: boolean;
}

export function FormAwareTextEditor<FormShape extends FieldValues>({
  fieldName,
  label,
  required,
  control,
  rows,
  maxLength,
  validation,
  disabled: disabledExternal,
  omitBlockTypes,
  allowTableInsert = true,
  preserveNewLines = false,
  resetKey,
  autoFocus,
}: FormAwareTextEditorProps<FormShape>) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const disabled = !!(disabledExternal || disabledFromContext);

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{
        required: required ? `${label} is required` : false,
        validate: {
          maxLength: (value) => {
            if (maxLength && value && value.length > maxLength) {
              return `${label} cannot exceed ${maxLength} characters`;
            }
            return true;
          },
          ...validation,
        },
      }}
      render={({ field, fieldState, formState }) => {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="title-s">
              {label}
              {required && ' *'}
            </Typography>
            {getFieldErrorValue(fieldState, formState.isSubmitted) && (
              <Typography color="error" variant="body-xs">
                {getFieldErrorValue(fieldState, formState.isSubmitted)}
              </Typography>
            )}
            <Box
              sx={{
                border: disabled ? `1px solid ${COLORS.GRAY[200]}` : undefined,
                borderRadius: '4px',
                pointerEvents: disabled ? 'none' : undefined,
                opacity: disabled ? 0.7 : undefined,
              }}
            >
              <TextEditor
                key={`${fieldName}-${disabled ? 'disabled' : 'enabled'}-${resetKey ?? 0}`}
                initialValue={field.value || ''}
                initialValueKind="markdown"
                onUpdate={(value) => {
                  field.onChange(value);
                }}
                readOnly={!!disabled}
                autoFocus={autoFocus}
                omitBlockTypes={omitBlockTypes}
                allowTableInsert={allowTableInsert}
                preserveNewLines={preserveNewLines}
                innerSx={{
                  ...(disabled && { background: COLORS.GRAY[50] }),
                  // The minimum height must be on the content-editable element.
                  // A minimum height on a parent does not work: the editor
                  // wrappers use percentage heights, which resolve to auto
                  // against a parent that has only a minimum height. The
                  // element sets its own minimum height inline, so this
                  // override needs !important. content-box keeps the
                  // element's padding out of the row count.
                  ...(rows && {
                    [`& #${CONTENT_EDITABLE_ID}`]: {
                      boxSizing: 'content-box',
                      minHeight: `${rows * ROW_HEIGHT_PX}px !important`,
                    },
                  }),
                }}
              />
            </Box>
          </Box>
        );
      }}
    />
  );
}
